import { Box, CircularProgress, Divider, Popover, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import dayjs from "../../utils/dayjs";
import { usePerspective } from "../../contexts/app/provider";
import { GetTransformedRawParquetDocument } from "../../services/perspective_api/__generated__/perspective_server-types";
import { parseParquetData, FullParsedParquetData } from "../../utils/parquet";
import { RunSettings } from "../../services/appsync/types";
import {
  closeSalesOrderCellPopover,
  useSalesOrderCellPopoverTarget,
  SalesOrderCellPopoverTarget,
} from "./store";

// One underlying sales_order_master_daily row behind a Demand cell. The daily
// table is (sku, location, channel, order_placed_day, quantity) — no revenue
// or sku_name at that grain, so those aren't shown.
interface SalesOrderDetailRow {
  sku: string;
  location: string;
  channel: string | null;
  order_date: number | null;
  quantity: number;
  /** Uncapped row count for the cell (rows are capped server-side). */
  total_row_count: number | null;
}

interface FetchEntry {
  loading: boolean;
  error: string | null;
  rows: SalesOrderDetailRow[] | null;
}

// Module-level cache so reopening a cell never refetches within a session.
// Keyed by run + cell identity; a new run settings object is a new key.
const detailCache = new Map<string, FetchEntry>();

const toNumberOrNull = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v); // arrow Int64 arrives as bigint
  return Number.isNaN(n) ? null : n;
};

function arrowToRows(parsed: FullParsedParquetData): SalesOrderDetailRow[] {
  return parsed.arrowTable.toArray().map((r: any) => ({
    sku: String(r.sku ?? ""),
    location: String(r.location ?? ""),
    channel: r.channel != null ? String(r.channel) : null,
    order_date: toNumberOrNull(r.order_date),
    quantity: toNumberOrNull(r.quantity) ?? 0,
    total_row_count: toNumberOrNull(r.total_row_count),
  }));
}

const formatDate = (ms: number | null): string =>
  ms == null ? "—" : dayjs.utc(ms).format("MM/DD/YY");

const formatQty = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: 0 });

const FIELD_LABEL_SX = {
  fontSize: "10.5px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  color: (t: any) => t.palette.atomic.muted,
};

const FIELD_VALUE_SX = {
  fontSize: "12px",
  fontVariantNumeric: "tabular-nums",
  color: (t: any) => t.palette.atomic.ink,
};

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: "16px",
        py: "3px",
      }}
    >
      <Box sx={FIELD_LABEL_SX}>{label}</Box>
      <Box sx={FIELD_VALUE_SX}>{value}</Box>
    </Box>
  );
}

/**
 * Popover with the individual sales_order_master rows behind one Demand cell.
 *
 * Render-isolation contract: this component (and its module store) is the ONLY
 * thing that re-renders on open/close/data-arrival. It must never feed state
 * back into the pivot table — the clicked-cell outline is applied imperatively
 * to the anchor element for the same reason. Mirrors the PO cell popover.
 */
export function SalesOrderCellPopover({
  runSettings,
}: {
  runSettings: RunSettings | null;
}) {
  const target = useSalesOrderCellPopoverTarget();
  const { perspectiveApiService } = usePerspective();
  const theme = useTheme();
  const outlineColor = theme.palette.atomic.brandBlue;
  const [entry, setEntry] = useState<FetchEntry>({
    loading: false,
    error: null,
    rows: null,
  });

  // Outline the clicked cell while the popover is open (imperative on purpose
  // — see render-isolation contract above).
  useEffect(() => {
    if (!target) return;
    const el = target.anchorEl;
    const prevOutline = el.style.outline;
    const prevOffset = el.style.outlineOffset;
    el.style.outline = `2px solid ${outlineColor}`;
    el.style.outlineOffset = "-2px";
    return () => {
      el.style.outline = prevOutline;
      el.style.outlineOffset = prevOffset;
    };
  }, [target, outlineColor]);

  useEffect(() => {
    if (!target) return;
    // A late async continuation (parseParquetData can do a network fetch) must
    // not write into a popover that's since re-anchored to another cell.
    // unsubscribe() can't cancel an already-started handler, so gate every
    // setEntry on this effect-scoped flag. Cache writes are fine either way —
    // they're keyed by cell.
    let cancelled = false;
    const cacheKey = JSON.stringify([
      runSettings,
      target.segments,
      target.periodStartMs,
      target.dateHierarchy,
    ]);
    // Only completed fetches count as cache hits. A stale {loading:true} entry
    // (popover closed mid-flight) or an error entry must refetch, or reopening
    // would spin forever.
    const cached = detailCache.get(cacheKey);
    if (cached?.rows) {
      setEntry(cached);
      return;
    }
    const loadingEntry: FetchEntry = { loading: true, error: null, rows: null };
    detailCache.set(cacheKey, loadingEntry);
    setEntry(loadingEntry);

    const client = perspectiveApiService.getClient();
    const sub = client
      .subscribe({
        query: GetTransformedRawParquetDocument,
        variables: {
          datasetName: "sales_order_master_daily",
          transformId: "sales_order_details",
          folder: "outputs",
          runSettingsJson: runSettings ?? null,
          comparedToRunSettingsJson: null,
          format: "parquet" as const,
          params: {
            segments: target.segments,
            period_start: new Date(target.periodStartMs).toISOString(),
            date_hierarchy: target.dateHierarchy,
          },
        },
      })
      .subscribe({
        next: async (res: any) => {
          const parquetData = res?.data?.getTransformedRawParquet?.parquetData;
          if (!parquetData || typeof parquetData !== "string") return;
          try {
            const parsed = await parseParquetData(
              parquetData,
              !parquetData.startsWith("http")
            );
            const rows = arrowToRows(parsed as FullParsedParquetData);
            const done: FetchEntry = { loading: false, error: null, rows };
            detailCache.set(cacheKey, done);
            if (!cancelled) setEntry(done);
          } catch (e: any) {
            const failed: FetchEntry = {
              loading: false,
              error: String(e?.message || e),
              rows: null,
            };
            detailCache.set(cacheKey, failed);
            if (!cancelled) setEntry(failed);
          }
        },
        error: (err: any) => {
          const failed: FetchEntry = {
            loading: false,
            error: String(err?.message || err),
            rows: null,
          };
          detailCache.set(cacheKey, failed);
          if (!cancelled) setEntry(failed);
        },
      });
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [target, runSettings, perspectiveApiService]);

  if (!target) return null;

  return (
    <Popover
      open
      anchorEl={target.anchorEl}
      onClose={closeSalesOrderCellPopover}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            padding: "12px 14px",
            minWidth: 240,
            maxWidth: 320,
            border: "1px solid",
            borderColor: (t) => t.palette.atomic.border,
            borderRadius: "8px",
          },
        },
      }}
    >
      <SalesOrderCellPopoverContent target={target} entry={entry} />
    </Popover>
  );
}

function HeaderChip() {
  return (
    <Box
      sx={{
        display: "inline-block",
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        borderRadius: "4px",
        padding: "2px 7px",
        mb: "8px",
        backgroundColor: (t) => t.palette.atomic.blue1,
        color: (t) => t.palette.atomic.brandBlueDark,
      }}
    >
      SALES ORDERS
    </Box>
  );
}

function SalesOrderCellPopoverContent({
  target,
  entry,
}: {
  target: SalesOrderCellPopoverTarget;
  entry: FetchEntry;
}) {
  if (entry.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: "12px" }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (entry.error) {
    return (
      <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.red1 }}>
        Couldn't load sales orders: {entry.error}
      </Box>
    );
  }
  const rows = entry.rows ?? [];
  if (rows.length === 0) {
    return (
      <Box>
        <HeaderChip />
        <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.muted }}>
          No sales orders for this cell.
        </Box>
      </Box>
    );
  }

  const totalCount = rows[0]?.total_row_count ?? rows.length;

  return (
    <Box>
      <HeaderChip />
      {totalCount > rows.length && (
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: (t) => t.palette.atomic.muted,
            mb: "6px",
          }}
        >
          Showing top {rows.length} of {formatQty(totalCount)} by quantity
        </Box>
      )}
      <Box sx={{ maxHeight: 320, overflowY: "auto", pr: "2px" }}>
        {rows.map((row, i) => (
          <Box key={`${row.sku}-${row.location}-${row.order_date}-${i}`}>
            {i > 0 && <Divider sx={{ my: "8px" }} />}
            <Box
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                color: (t) => t.palette.atomic.ink,
                mb: "6px",
              }}
            >
              {row.sku}
            </Box>
            {row.channel && <FieldRow label="Channel" value={row.channel} />}
            <FieldRow label="Location" value={row.location} />
            <FieldRow label="Date" value={formatDate(row.order_date)} />
            <FieldRow label="Quantity" value={formatQty(row.quantity)} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
